import {Form, Formik, FormikProps} from 'formik';
import {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import * as Yup from 'yup';

import {settingsApi} from 'api';
import {
    Button, Card, DataWrapper, ExternalIntegrationPurposeSelect, InputFormik, PasswordInputFormik,
    PredefinedNotifierType, ToggleFormik, UnsavedChangesLeavePrompt
} from 'components';
import {ExternalIntegrationPurpose, ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {WhatsappSettings} from 'types';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.WHATSAPP;
const COMMON_T_KEYS = TRANSLATION_KEYS.COMMON;

type FormTemplateProps = {
    loading: boolean;
    maskedAccessToken: string;
} & FormikProps<WhatsappSettings>;

const FIELDS = {
    ENABLED: 'enabled',
    PHONE_NUMBER_ID: 'phoneNumberId',
    ACCESS_TOKEN: 'authentication.accessToken',
};

const FormTemplate: React.FC<FormTemplateProps> = ({
    loading, values, initialValues, submitForm, isValid, maskedAccessToken
}) => {
    const {t} = useTranslation();
    const hasNoChanges = useMemo(() => dataUtils.isEqualValues(values, initialValues), [initialValues, values]);

    return (
        <Form className='tw-relative'>
            <DataWrapper isLoading={loading} hideContentOnLoading={false}>
                <Card>
                    <div className='tw-max-w-104'>
                        <ToggleFormik
                            name={FIELDS.ENABLED}
                            label={t(T_KEYS.LABELS.ENABLED)}
                            className='tw-my-4'
                        />
                        <InputFormik
                            name={FIELDS.PHONE_NUMBER_ID}
                            label={t(T_KEYS.LABELS.PHONE_NUMBER_ID)}
                            className='tw-w-full'
                            required={values.enabled}
                            spacing='top'
                        />
                        <PasswordInputFormik
                            name={FIELDS.ACCESS_TOKEN}
                            label={t(T_KEYS.LABELS.ACCESS_TOKEN)}
                            className='tw-w-full tw-mt-4'
                            spacing='bottom'
                            required={values.enabled && !maskedAccessToken}
                            autoComplete='one-time-code'
                            description={maskedAccessToken}
                        />

                        <div className='tw-text-right tw-mt-controls'>
                            <Button
                                color='primary'
                                disabled={hasNoChanges || !isValid}
                                loading={loading}
                                onClick={submitForm}
                            >
                                {t(COMMON_T_KEYS.BUTTONS.SAVE)}
                            </Button>
                        </div>
                    </div>
                </Card>
            </DataWrapper>
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Form>
    );
};

export const WhatsappSettingsPage: React.FC = () => {
    const {t} = useTranslation();
    const [integrationPurpose, setIntegrationPurpose] = useState<ExternalIntegrationPurpose>(ExternalIntegrationPurpose.ClientConnect);
    const [maskedAccessToken, setMaskedAccessToken] = useState('');
    const [initialValues, setInitialValues] = useState<WhatsappSettings>({
        enabled: false,
        phoneNumberId: '',
        authentication: {
            accessToken: ''
        }
    });

    const {
        send: fetchSettingsFn, data: settings, loading: fetchLoading
    } = useApiData(settingsApi.getWhatsapp, {notificationType: PredefinedNotifierType.FetchError});

    const fetchSettings = useCallback(() => {
        fetchSettingsFn(integrationPurpose);
    }, [integrationPurpose, fetchSettingsFn]);

    const {send: updateSettings, loading: updateLoading} = useApiData(settingsApi.updateWhatsapp,
        {onSuccess: fetchSettings, notificationType: PredefinedNotifierType.Save});

    const validationSchema = useMemo(() => Yup.object().shape({
        [FIELDS.PHONE_NUMBER_ID]: Yup.string().when(FIELDS.ENABLED, {
            is: true,
            then: (schema) => schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
        }),
        authentication: Yup.object().shape({
            accessToken: Yup.string().when(FIELDS.ENABLED, {
                is: true,
                then: (schema) => maskedAccessToken ? schema :
                    schema.required(apiUtils.getErrorMessageByCode(t, {code: ValidationCode.Required})),
            })
        })
    }), [t, maskedAccessToken]);

    const onSubmit = useCallback((values: WhatsappSettings) => {
        updateSettings(integrationPurpose, values);
    }, [integrationPurpose, updateSettings]);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    useEffect(() => {
        setMaskedAccessToken(settings?.authentication?.accessToken);
        setInitialValues({
            enabled: !!settings?.enabled,
            phoneNumberId: settings?.phoneNumberId || '',
            authentication: {
                accessToken: ''
            }
        });
    }, [settings]);

    return (
        <Fragment>
            <ExternalIntegrationPurposeSelect
                className='tw-max-w-104 tw-mb-4'
                disabled
                value={integrationPurpose}
                onChange={setIntegrationPurpose}
            />
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={onSubmit}
            >
                {(props) => <FormTemplate {...props} maskedAccessToken={maskedAccessToken} loading={fetchLoading || updateLoading} />}
            </Formik>
        </Fragment>

    );
};